import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { Mail, Loader2, CheckCircle2, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export default function ContactDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useRef(null);

  useEffect(() => {
    if (open) {
      startedAt.current = Date.now();
      setDone(false);
    } else {
      // small delay so the dialog close animation doesn't show empty state
      setTimeout(() => {
        setName(""); setEmail(""); setMessage(""); setWebsite("");
      }, 250);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!name.trim() || !email.trim() || message.trim().length < 4) {
      toast.error(t("contact.fillAll"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        website,
        started_at: startedAt.current,
      });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(t("contact.tooMany"));
      else toast.error(t("contact.errSend"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card" data-testid="contact-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="size-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Mail className="size-4" />
            </div>
            <DialogTitle className="font-serif text-2xl tracking-tight">{t("contact.title")}</DialogTitle>
          </div>
          <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
            {t("contact.about")}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center" data-testid="contact-done">
            <CheckCircle2 className="size-12 text-primary mx-auto mb-3" />
            <p className="font-serif text-lg font-medium">{t("contact.doneTitle")}</p>
            <p className="text-sm text-muted-foreground mt-1.5">{t("contact.doneLead")}</p>
            <Button variant="secondary" onClick={() => onOpenChange(false)} className="mt-5" data-testid="contact-close-btn">
              {t("common.close")}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">
                {t("contact.name")}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                required
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">
                {t("contact.email")}
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">
                {t("contact.message")}
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                maxLength={4000}
                required
                className="resize-none"
                data-testid="contact-message"
              />
            </div>

            {/* Honeypot – hidden from real users, bots will fill it */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </label>
            </div>

            <p className="text-[11px] text-muted-foreground pt-1 flex items-center gap-1.5">
              <Stethoscope className="size-3" />
              {t("contact.privacy")}
            </p>

            <Button type="submit" disabled={loading} className="w-full" data-testid="contact-submit-btn">
              {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <Mail className="size-4 mr-2" />}
              {t("contact.send")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
